import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { api } from "@/lib/api";

/**
 * Cloudflare Turnstile bot-shield widget.
 *
 * Renders the Cloudflare-managed challenge in front of the login / signup
 * forms. Fetches the site key from `/api/public/config` on mount — if the
 * server hasn't set `TURNSTILE_SITE_KEY`, the widget renders nothing and
 * the form works exactly like before (dev-mode fallback).
 *
 * Usage:
 *   const captchaRef = useRef();
 *   <TurnstileWidget ref={captchaRef} action="login" onToken={setToken} />
 *   // after a 400/403 from the API, call captchaRef.current?.reset() to
 *   // get a fresh single-use token (tokens can't be reused).
 */
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise;
function loadTurnstile() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.turnstile));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

// Cache the config lookup so every mount doesn't hit the API again.
// NOTE: Turnstile is DISABLED at the app level (2026-09-05). We short-
// circuit to null so the widget never renders, regardless of what the
// backend `/api/public/config` returns. To re-enable, delete the two-line
// override below and let the real fetch run again.
let configPromise;
function fetchTurnstileConfig() {
  if (configPromise) return configPromise;
  configPromise = Promise.resolve(null);
  return configPromise;
  // eslint-disable-next-line no-unreachable
  configPromise = api
    .get("/public/config")
    .then((r) => r.data?.turnstile_site_key || null)
    .catch(() => null);
  return configPromise;
}

const TurnstileWidget = forwardRef(function TurnstileWidget(
  { action, onToken, onError, className = "" },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [siteKey, setSiteKey] = useState(null);
  const [ready, setReady] = useState(false);

  // Expose an imperative reset() so the form can call it after a rejection.
  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (widgetIdRef.current != null && window.turnstile) {
          try {
            window.turnstile.reset(widgetIdRef.current);
          } catch {
            /* ignore */
          }
        }
      },
      isEnabled: () => !!siteKey,
    }),
    [siteKey]
  );

  useEffect(() => {
    let cancelled = false;
    fetchTurnstileConfig().then((key) => {
      if (!cancelled) {
        setSiteKey(key);
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !siteKey || !containerRef.current) return undefined;
    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: "auto",
          appearance: "interaction-only",
          callback: (token) => onToken?.(token),
          "expired-callback": () => onToken?.(null),
          "error-callback": (code) => {
            onToken?.(null);
            onError?.(code);
          },
        });
      })
      .catch((err) => onError?.(err));

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [ready, siteKey, action, onToken, onError]);

  // Not enabled → render nothing. Form works as before.
  if (!ready) return null;
  if (!siteKey) return null;
  return (
    <div
      ref={containerRef}
      className={className}
      data-testid={`turnstile-widget-${action}`}
      aria-label="Security verification"
    />
  );
});

export default TurnstileWidget;
